import React from 'react';
import { Box, Alert } from '@mui/material';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  BarElement
} from 'chart.js';

// Dashboard components
import DashboardHeader from '../components/dashboard/components/DashboardHeader';
import DashboardOverviewSection from '../components/dashboard/components/DashboardOverviewSection';
import DeadlinesAndNotificationsSection from '../components/dashboard/components/DeadlinesAndNotificationsSection';
import LoadingState from '../components/dashboard/components/LoadingState';
import ErrorState from '../components/dashboard/components/ErrorState';
import ProjectOverviewComponent from '../components/dashboard/components/ProjectOverviewComponent';

// Dashboard utils
import useDashboardMain from '../components/dashboard/hooks/useDashboardMain';
import { getProgressColor, statusColors } from '../components/dashboard/utils/formatters';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  BarElement
);

const Dashboard = () => {
  const {
    displayName,
    loading,
    projects,
    activeProjects,
    upcomingDeadlines,
    error,
    selectedProject,
    handleFilterChange
  } = useDashboardMain();

  const dashboardOverviewData = {
    runningProjects: activeProjects,
    projects: projects,
    statusDistribution: {},
    todaysTasks: [],
    upcomingDeadlines: upcomingDeadlines,
    projectPerformance: {
      activeProjects: activeProjects.length,
      completedProjects: 0,
      todaysTasks: 0,
      upcomingDeadlines: 0
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }
  
  return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      p: { xs: 2, sm: 3, md: 3.5 },
      background: 'linear-gradient(180deg, #EFF6FF 0%, #F8FAFC 120px)',
      backgroundColor: '#F8FAFC',
      maxWidth: '100vw',
      overflowX: 'hidden',
      mx: 'auto',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <DashboardHeader />

      {/* Error message if any */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            width: '100%', 
            maxWidth: '100%',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          {error}
        </Alert>
      )}
      
      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Project Overview Component */}
        <ProjectOverviewComponent />
        
        {/* Dashboard Overview Section */}
        <DashboardOverviewSection
            selectedProject={selectedProject}
            handleFilterChange={handleFilterChange}
            dashboardOverviewData={dashboardOverviewData}
            activeProjects={activeProjects}
            projects={projects}
            statusCounts={{}}
            todayTasks={[]}
            displayName={displayName}
            statusColors={statusColors}
            getProgressColor={getProgressColor}
          />

        {/* Deadlines and Notifications */}
        <DeadlinesAndNotificationsSection
            upcomingDeadlines={upcomingDeadlines}
            dashboardOverviewData={dashboardOverviewData}
            projects={projects}
            notifications={[]}
            activeProjects={activeProjects}
          />
      </Box>
    </Box>
  );
};

export default Dashboard;
